import { useState, useEffect, useRef } from "react";

const STARFISH_DATA = {
  name: "불가사리",
  ringName: "THE UNCHAINABLE",
  title: "심해 콜로세움 무패 챔피언",
  origin: "동해 울진 앞바다",
  weight: "5kg (유동적)",
  height: "팔 포함 45cm (가변)",
  style: "무형체 서브미션",
  winRate: "???.?%",
  bio: [
    "밧줄로 묶을 수 없다.",
    "의자에 앉힐 수도 없다.",
    "팔이 잘려도 다시 자란다.",
    "은퇴 경기가 불가능한 유일한 파이터.",
  ],
  quote: "나를 묶을 수 있다고? 의자도, 밧줄도, 계약서도 소용없다.",
  likes: ["조개", "느린 움직임", "재생", "체외소화", "의자 탈출"],
  dislikes: ["밧줄", "의자", "건조한 곳", "민물", "빠른 판정"],
  moves: [
    {
      name: "TUBE FEET CHOKEHOLD",
      type: "GRAB",
      power: 3,
      speed: 2,
      desc: "관족 수천 개로 서서히 감싸는 서브미션. 어디가 잡힌 건지도 모른 채 탭아웃.",
      color: "#F59E0B",
    },
    {
      name: "OSSICLE MELTDOWN",
      type: "COUNTER",
      power: 2,
      speed: 4,
      desc: '골편 메시를 풀어 모든 잠금기 무효화. 해설자: "또 빠져나갔습니다!"',
      color: "#60A5FA",
    },
    {
      name: "CARDIAC STOMACH DROP",
      type: "FINISHER",
      power: 5,
      speed: 1,
      desc: "위를 체외로 뒤집어 상대를 소화. 실제 불가사리 섭식 방식이라 더 끔찍함.",
      color: "#EF4444",
    },
    {
      name: "REGENERATION ROUND",
      type: "PASSIVE",
      power: 0,
      speed: 5,
      desc: "팔 하나 잘려도 다음 시즌에 멀쩡하게 복귀. 은퇴 경기가 불가능한 이유.",
      color: "#34D399",
    },
  ],
  stats: {
    power: 3,
    speed: 1,
    defense: 5,
    technique: 4,
    recovery: 5,
    escape: 5,
  },
  lore: [
    {
      title: "의자 사건",
      text: '2024년, 한 예능 프로그램에서 밧줄로 묶어보겠다는 실험이 진행됐다. 작은 나무 의자에 묶였으나 수분 내 탈출. 자막: "소용없다." 이후 격투계에서 스카우트 제의를 받았다는 소문이 있다.',
    },
    {
      title: "무형체의 비밀",
      text: '뼈대가 석회질 골편(ossicle)의 느슨한 메시 구조로 되어 있어 몸 자체를 액체처럼 변형 가능. 관절기가 구조적으로 성립하지 않는다. 해설석 코멘트: "관절기가 안 먹혀요. 어떻게 이기라는 겁니까?"',
    },
    {
      title: "심장이 없다",
      text: "심장도, 뇌도 없다. 급소가 존재하지 않는다는 뜻이다. 수관계(water vascular system)로 움직이므로 KO 판정 자체가 논란이다. 현재 심해 콜로세움 규정위원회에서 검토 중.",
    },
  ],
};

function StatBar({ label, value, maxVal = 5, color }: { label: string; value: number; maxVal?: number; color: string }) {
  const [filled, setFilled] = useState(false);

  useEffect(() => {
    setFilled(false);
    const raf = requestAnimationFrame(() => {
      requestAnimationFrame(() => setFilled(true));
    });
    return () => cancelAnimationFrame(raf);
  }, [value]);

  // Total animation duration spread across segments
  const totalDuration = 0.8;
  const perSegment = totalDuration / maxVal;

  return (
    <div
      style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}
    >
      <div
        style={{
          width: 56,
          fontSize: 10,
          color: "#888",
          fontFamily: "monospace",
          textAlign: "right",
          letterSpacing: 1,
        }}
      >
        {label}
      </div>
      <div style={{ flex: 1, display: "flex", gap: 3 }}>
        {Array.from({ length: maxVal }).map((_, i) => {
          const active = i < value;
          const show = filled && active;
          return (
            <div
              key={i}
              style={{
                flex: 1,
                height: 10,
                borderRadius: 1,
                position: "relative",
                background: "rgba(255,255,255,0.06)",
                overflow: "hidden",
              }}
            >
              <div
                style={{
                  position: "absolute",
                  inset: 0,
                  background: color,
                  boxShadow: `0 0 6px ${color}44`,
                  transformOrigin: "left",
                  transform: show ? "scaleX(1)" : "scaleX(0)",
                  transition: show
                    ? `transform 0.25s cubic-bezier(0.25, 0.46, 0.45, 0.94) ${i * perSegment}s`
                    : "none",
                }}
              />
            </div>
          );
        })}
      </div>
    </div>
  );
}

function Tag({ label, color }: { label: string; color: string }) {
  const [hovered, setHovered] = useState(false);
  return (
    <span
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        fontSize: 11,
        color: color,
        background: hovered ? `${color}30` : `${color}11`,
        padding: "4px 10px",
        borderRadius: 3,
        border: `1px solid ${hovered ? `${color}55` : `${color}22`}`,
        cursor: "default",
        transition: "all 0.2s ease",
        textShadow: hovered ? `0 0 8px ${color}66` : "none",
      }}
    >
      {label}
    </span>
  );
}

function MiniStatBar({ label, value, maxVal = 5, color }: { label: string; value: number; maxVal?: number; color: string }) {
  const [filled, setFilled] = useState(false);

  useEffect(() => {
    setFilled(false);
    const raf = requestAnimationFrame(() => {
      requestAnimationFrame(() => setFilled(true));
    });
    return () => cancelAnimationFrame(raf);
  }, [value]);

  const totalDuration = 0.6;
  const perSegment = totalDuration / maxVal;

  return (
    <div style={{ display: "flex", alignItems: "center", gap: 4, fontSize: 9 }}>
      <span style={{ color, fontFamily: "monospace" }}>{label}</span>
      <div style={{ display: "flex", gap: 2 }}>
        {Array.from({ length: maxVal }).map((_, i) => {
          const active = i < value;
          const show = filled && active;
          return (
            <div
              key={i}
              style={{
                width: 8,
                height: 8,
                borderRadius: 1,
                position: "relative",
                background: "rgba(255,255,255,0.06)",
                overflow: "hidden",
              }}
            >
              <div
                style={{
                  position: "absolute",
                  inset: 0,
                  background: color,
                  boxShadow: `0 0 4px ${color}44`,
                  transformOrigin: "left",
                  transform: show ? "scaleX(1)" : "scaleX(0)",
                  transition: show
                    ? `transform 0.2s cubic-bezier(0.25, 0.46, 0.45, 0.94) ${i * perSegment}s`
                    : "none",
                }}
              />
            </div>
          );
        })}
      </div>
    </div>
  );
}

function MoveCard({ move, index, isActive, onClick }: { move: any; index: number; isActive: boolean; onClick: () => void }) {
  const typeIcons: Record<string, string> = {
    GRAB: "🤏",
    COUNTER: "🔄",
    FINISHER: "💀",
    PASSIVE: "♻️",
  };
  return (
    <div
      onClick={onClick}
      style={{
        background: isActive ? `${move.color}15` : "rgba(255,255,255,0.02)",
        border: `1px solid ${
          isActive ? move.color + "66" : "rgba(255,255,255,0.06)"
        }`,
        borderLeft: `3px solid ${isActive ? move.color : "transparent"}`,
        padding: "10px 12px",
        cursor: "pointer",
        transition: "all 0.2s",
      }}
    >
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
          <span style={{ fontSize: 12 }}>{typeIcons[move.type]}</span>
          <span
            style={{
              fontFamily: "'Press Start 2P', monospace",
              fontSize: 9,
              color: move.color,
              letterSpacing: 0.5,
            }}
          >
            {move.name}
          </span>
        </div>
        <span
          style={{
            fontSize: 8,
            color: "#555",
            fontFamily: "monospace",
            background: "rgba(255,255,255,0.04)",
            padding: "2px 6px",
            borderRadius: 2,
          }}
        >
          {move.type}
        </span>
      </div>
      {isActive && (
        <div style={{ marginTop: 8, animation: "fadeSlide 0.25s ease" }}>
          <div
            style={{
              fontSize: 11,
              color: "#bbb",
              lineHeight: 1.5,
              marginBottom: 8,
            }}
          >
            {move.desc}
          </div>
          <div style={{ display: "flex", gap: 12 }}>
            <MiniStatBar label="PWR" value={move.power} color="#EF4444" />
            <MiniStatBar label="SPD" value={move.speed} color="#60A5FA" />
          </div>
        </div>
      )}
    </div>
  );
}

export default function StarfishProfile() {
  const [activeMove, setActiveMove] = useState(0);
  const [activeLore, setActiveLore] = useState(0);
  const [tab, setTab] = useState("profile");
  const [scanLine, setScanLine] = useState(0);
  const [glitch, setGlitch] = useState(false);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    setTimeout(() => setLoaded(true), 100);
    const interval = setInterval(() => {
      if (Math.random() > 0.95) {
        setGlitch(true);
        setTimeout(() => setGlitch(false), 150);
      }
    }, 2000);
    return () => clearInterval(interval);
  }, []);

  const d = STARFISH_DATA;

  return (
    <div
      style={{
        minHeight: "100vh",
        background: "#08080C",
        color: "#fff",
        fontFamily: "'Noto Sans KR', sans-serif",
        position: "relative",
        overflow: "hidden",
      }}
    >
      <link
        href="https://fonts.googleapis.com/css2?family=Press+Start+2P&family=Noto+Sans+KR:wght@300;400;700;900&family=Black+Han+Sans&display=swap"
        rel="stylesheet"
      />
      <style>{`        
        @keyframes fadeSlide { from { opacity: 0; transform: translateY(-4px); } to { opacity: 1; transform: translateY(0); } }        
        @keyframes scanMove { from { top: -2px; } to { top: 100%; } }        
        @keyframes flicker { 0%,100%{opacity:1} 92%{opacity:1} 93%{opacity:0.4} 94%{opacity:1} 96%{opacity:0.7} 97%{opacity:1} }        
        @keyframes pulseGlow { 0%,100%{box-shadow: 0 0 20px rgba(232,168,124,0.1)} 50%{box-shadow: 0 0 40px rgba(232,168,124,0.25)} }        
        @keyframes starfishWobble { 0%,40%,100%{transform:rotate(0deg)} 45%{transform:rotate(5deg)} 50%{transform:rotate(-4deg)} 55%{transform:rotate(3deg)} 60%{transform:rotate(-2deg)} 65%{transform:rotate(0deg)} }        
        @keyframes marquee { 0%{transform:translateX(100%)} 100%{transform:translateX(-200%)} }        
        @keyframes blink { 0%,100%{opacity:1} 50%{opacity:0} }        
        @keyframes textShimmer { 0%{background-position: -200% center} 100%{background-position: 200% center} }        
        @keyframes starFloat { 0%,100%{transform:translateY(0px) rotate(0deg)} 25%{transform:translateY(-8px) rotate(2deg)} 75%{transform:translateY(4px) rotate(-2deg)} }        
        @keyframes glowPulse { 0%,100%{filter: drop-shadow(0 0 20px rgba(239,68,68,0.3))} 50%{filter: drop-shadow(0 0 40px rgba(249,115,22,0.5))} }        
        @keyframes fadeUp { 0%{opacity:0; transform:translateY(20px)} 100%{opacity:1; transform:translateY(0)} }        
        @keyframes chainSwayTL {        
          0% { transform: rotate(45deg) translate(0,0); opacity: 0.75; filter: brightness(1); }        
          6% { transform: rotate(42deg) translate(1px,-1px); }        
          12% { transform: rotate(48deg) translate(-1px,1px); }        
          18% { transform: rotate(43deg) translate(1px,0); }        
          24% { transform: rotate(47deg) translate(-1px,1px); }        
          30% { transform: rotate(44deg) translate(0,0); }        
          36% { transform: rotate(46deg) translate(0,0); }        
          42% { transform: rotate(45deg) translate(0,0); opacity: 0.75; }        
          /* tension builds */        
          46% { transform: rotate(40deg) translate(3px,-2px); opacity: 0.85; }        
          48% { transform: rotate(50deg) translate(-3px,2px); }        
          50% { transform: rotate(38deg) translate(4px,-3px); }        
          52% { transform: rotate(52deg) translate(-4px,3px); }        
          54% { transform: rotate(37deg) translate(4px,-2px); }        
          56% { transform: rotate(53deg) translate(-3px,2px); }        
          58% { transform: rotate(45deg) translate(0,0); opacity: 0.9; }        
          /* SNAP! */        
          60% { transform: rotate(45deg) translate(0,0); opacity: 1; filter: brightness(1); }        
          63% { transform: rotate(58deg) translate(-20px,-18px); opacity: 0.5; filter: brightness(0.9); }        
          70% { transform: rotate(70deg) translate(-40px,-35px); opacity: 0; filter: brightness(0.8); }        
          92% { opacity: 0; }        
          100% { transform: rotate(45deg) translate(0,0); opacity: 0.75; filter: brightness(1); }        
        }        
        @keyframes chainSwayBR {        
          0% { transform: rotate(45deg) translate(0,0); opacity: 0.75; filter: brightness(1); }        
          6% { transform: rotate(48deg) translate(-1px,1px); }        
          12% { transform: rotate(42deg) translate(1px,-1px); }        
          18% { transform: rotate(47deg) translate(-1px,0); }        
          24% { transform: rotate(43deg) translate(1px,-1px); }        
          30% { transform: rotate(46deg) translate(0,0); }        
          36% { transform: rotate(44deg) translate(0,0); }        
          42% { transform: rotate(45deg) translate(0,0); opacity: 0.75; }        
          /* tension builds */        
          48% { transform: rotate(50deg) translate(-3px,2px); opacity: 0.85; }        
          50% { transform: rotate(38deg) translate(4px,-3px); }        
          52% { transform: rotate(52deg) translate(-4px,3px); }        
          54% { transform: rotate(37deg) translate(4px,-2px); }        
          56% { transform: rotate(53deg) translate(-3px,2px); }        
          58% { transform: rotate(39deg) translate(3px,-2px); }        
          60% { transform: rotate(45deg) translate(0,0); opacity: 0.9; }        
          /* SNAP! (slightly delayed) */        
          62% { transform: rotate(45deg) translate(0,0); opacity: 1; filter: brightness(1); }        
          65% { transform: rotate(32deg) translate(20px,18px); opacity: 0.5; filter: brightness(0.9); }        
          72% { transform: rotate(20deg) translate(40px,35px); opacity: 0; filter: brightness(0.8); }        
          92% { opacity: 0; }        
          100% { transform: rotate(45deg) translate(0,0); opacity: 0.75; filter: brightness(1); }        
        }        
        @keyframes chainSpark {        
          0%,58% { opacity: 0; transform: scale(0); }        
          60% { opacity: 1; transform: scale(1); }        
          62% { opacity: 0.8; transform: scale(1.8); }        
          66% { opacity: 0; transform: scale(2.5); }        
          100% { opacity: 0; transform: scale(0); }        
        }        
        * { scrollbar-width: thin; scrollbar-color: #333 transparent; }        
      `}</style>

      {/* CRT scanline overlay */}
      <div
        style={{
          position: "fixed",
          inset: 0,
          pointerEvents: "none",
          zIndex: 100,
          background:
            "repeating-linear-gradient(0deg, transparent, transparent 2px, rgba(0,0,0,0.08) 2px, rgba(0,0,0,0.08) 4px)",
        }}
      />

      {/* Scan line animation */}
      <div
        style={{
          position: "fixed",
          left: 0,
          right: 0,
          height: 2,
          zIndex: 101,
          pointerEvents: "none",
          background:
            "linear-gradient(90deg, transparent, rgba(232,168,124,0.15), transparent)",
          animation: "scanMove 4s linear infinite",
        }}
      />

      {/* Top bar - arcade style */}
      <div
        style={{
          background: "linear-gradient(90deg, #E8A87C11, #D4775B22, #E8A87C11)",
          borderBottom: "1px solid #E8A87C33",
          padding: "8px 16px",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <div
          style={{
            fontFamily: "'Press Start 2P', monospace",
            fontSize: 8,
            color: "#E8A87C",
            letterSpacing: 2,
          }}
        >
          ▶ FIGHTER SELECT
        </div>
        <div
          style={{
            fontFamily: "'Press Start 2P', monospace",
            fontSize: 7,
            color: "#555",
          }}
        >
          CREDIT: <span style={{ color: "#E8A87C" }}>99</span>
        </div>
      </div>

      {/* Scrolling announcement bar */}
      <div
        style={{
          background: "#0C0C12",
          borderBottom: "1px solid #1a1a1a",
          padding: "5px 0",
          overflow: "hidden",
          whiteSpace: "nowrap",
        }}
      >
        <div
          style={{
            fontFamily: "'Press Start 2P', monospace",
            fontSize: 7,
            color: "#E8A87C88",
            animation: "marquee 20s linear infinite",
            display: "inline-block",
          }}
        >
          ★ 심해 콜로세움 무패 챔피언 ★ 밧줄로 묶을 수 없다 ★ 의자에 앉힐 수도
          없다 ★ 팔이 잘려도 다시 자란다 ★ 은퇴 경기가 불가능한 유일한 파이터 ★
          CARDIAC STOMACH DROP 피니셔 주의 ★
        </div>
      </div>

      <div style={{ maxWidth: 800, margin: "0 auto", padding: "0 16px" }}>
        {/* Character header */}
        <div
          style={{
            padding: "24px 0 16px",
            display: "flex",
            gap: 20,
            alignItems: "flex-start",
            opacity: loaded ? 1 : 0,
            transform: loaded ? "translateY(0)" : "translateY(20px)",
            transition: "all 0.8s cubic-bezier(0.16, 1, 0.3, 1)",
          }}
        >
          {/* Character portrait wrapper */}
          <div style={{ position: "relative", flexShrink: 0 }}>
            <div
              style={{
                width: 140,
                height: 140,
                background: "linear-gradient(135deg, #E8A87C11, #D4775B22)",
                border: "2px solid #E8A87C44",
                borderRadius: 4,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                position: "relative",
                overflow: "hidden",
                animation: "pulseGlow 3s ease infinite",
                filter: glitch ? "hue-rotate(90deg) brightness(1.3)" : "none",
                transition: "filter 0.1s",
              }}
            >
              <div
                style={{
                  position: "absolute",
                  top: "-5.5%",
                  left: "-5.5%",
                  transform: "translate(-50%, -50%)",
                  animation:
                    "starFloat 4s ease-in-out infinite, glowPulse 3s ease-in-out infinite",
                }}
              >
                <svg
                  width="150"
                  height="150"
                  viewBox="0 0 180 180"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <defs>
                    <filter
                      id="sf_ii"
                      x="4.72591"
                      y="6.96049"
                      width="173.548"
                      height="174.124"
                      filterUnits="userSpaceOnUse"
                      colorInterpolationFilters="sRGB"
                    >
                      <feFlood floodOpacity="0" result="BackgroundImageFix" />
                      <feBlend
                        mode="normal"
                        in="SourceGraphic"
                        in2="BackgroundImageFix"
                        result="shape"
                      />
                      <feColorMatrix
                        in="SourceAlpha"
                        type="matrix"
                        values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
                        result="hardAlpha"
                      />
                      <feOffset dy="-6" />
                      <feGaussianBlur stdDeviation="5" />
                      <feComposite
                        in2="hardAlpha"
                        operator="arithmetic"
                        k2="-1"
                        k3="1"
                      />
                      <feColorMatrix
                        type="matrix"
                        values="0 0 0 0 0.446421 0 0 0 0 0.0338035 0 0 0 0 0.205727 0 0 0 0.75 0"
                      />
                      <feBlend mode="normal" in2="shape" result="e1" />
                      <feColorMatrix
                        in="SourceAlpha"
                        type="matrix"
                        values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
                        result="hardAlpha"
                      />
                      <feOffset dy="4" />
                      <feGaussianBlur stdDeviation="2" />
                      <feComposite
                        in2="hardAlpha"
                        operator="arithmetic"
                        k2="-1"
                        k3="1"
                      />
                      <feColorMatrix
                        type="matrix"
                        values="0 0 0 0 1 0 0 0 0 0.95607 0 0 0 0 0.670523 0 0 0 0.55 0"
                      />
                      <feBlend mode="normal" in2="e1" result="e2" />
                    </filter>
                    <linearGradient
                      id="sg0"
                      x1="119"
                      y1="78"
                      x2="119"
                      y2="88"
                      gradientUnits="userSpaceOnUse"
                    >
                      <stop stopColor="#4F1516" />
                      <stop offset="1" stopColor="#6C2E2E" />
                    </linearGradient>
                    <linearGradient
                      id="sg1"
                      x1="91.8386"
                      y1="120.04"
                      x2="93.1252"
                      y2="113.159"
                      gradientUnits="userSpaceOnUse"
                    >
                      <stop stopColor="#4F1516" />
                      <stop offset="1" stopColor="#6C2E2E" />
                    </linearGradient>
                    <linearGradient
                      id="sg2"
                      x1="66.5"
                      y1="78"
                      x2="66.5"
                      y2="88"
                      gradientUnits="userSpaceOnUse"
                    >
                      <stop stopColor="#4F1516" />
                      <stop offset="1" stopColor="#6C2E2E" />
                    </linearGradient>
                    <linearGradient
                      id="sg3"
                      x1="117"
                      y1="93.5455"
                      x2="117"
                      y2="112.546"
                      gradientUnits="userSpaceOnUse"
                    >
                      <stop stopColor="#6C2D2D" />
                      <stop offset="1" stopColor="#4F1516" />
                    </linearGradient>
                    <linearGradient
                      id="sg4"
                      x1="68"
                      y1="93.5455"
                      x2="68"
                      y2="112.546"
                      gradientUnits="userSpaceOnUse"
                    >
                      <stop stopColor="#6C2D2D" />
                      <stop offset="1" stopColor="#4F1516" />
                    </linearGradient>
                    <linearGradient
                      id="sg5"
                      x1="92.25"
                      y1="86.75"
                      x2="92.25"
                      y2="102.25"
                      gradientUnits="userSpaceOnUse"
                    >
                      <stop stopColor="#4B3D3D" />
                      <stop offset="1" stopColor="#1D1515" />
                    </linearGradient>
                    <linearGradient
                      id="sg6"
                      x1="64.5"
                      y1="84"
                      x2="64.5"
                      y2="122"
                      gradientUnits="userSpaceOnUse"
                    >
                      <stop stopColor="#1C1414" />
                      <stop offset="1" stopColor="#4D3F3F" />
                    </linearGradient>
                    <linearGradient
                      id="sg7"
                      x1="119.5"
                      y1="84"
                      x2="119.5"
                      y2="122"
                      gradientUnits="userSpaceOnUse"
                    >
                      <stop stopColor="#1C1414" />
                      <stop offset="1" stopColor="#4D3F3F" />
                    </linearGradient>
                    <linearGradient
                      id="sg8"
                      x1="84.5713"
                      y1="78.7871"
                      x2="84.5713"
                      y2="116.287"
                      gradientUnits="userSpaceOnUse"
                    >
                      <stop stopColor="white" stopOpacity="0.8" />
                      <stop offset="1" stopColor="white" stopOpacity="0" />
                    </linearGradient>
                    <linearGradient
                      id="sg9"
                      x1="140.355"
                      y1="78.7871"
                      x2="140.355"
                      y2="116.287"
                      gradientUnits="userSpaceOnUse"
                    >
                      <stop stopColor="white" stopOpacity="0.8" />
                      <stop offset="1" stopColor="white" stopOpacity="0" />
                    </linearGradient>
                    <clipPath id="sgClip">
                      <rect width="184" height="178" fill="white" />
                    </clipPath>
                    <mask
                      id="sgMask"
                      style={{ maskType: "alpha" }}
                      maskUnits="userSpaceOnUse"
                      x="42"
                      y="84"
                      width="100"
                      height="38"
                    >
                      <path
                        d="M42 89C42 86.2386 44.2386 84 47 84H82C84.7614 84 87 86.2386 87 89V102C87 113.046 78.0457 122 67 122H62C50.9543 122 42 113.046 42 102V89Z"
                        fill="url(#sg6)"
                      />
                      <path
                        d="M97 89C97 86.2386 99.2386 84 102 84H137C139.761 84 142 86.2386 142 89V102C142 113.046 133.046 122 122 122H117C105.954 122 97 113.046 97 102V89Z"
                        fill="url(#sg7)"
                      />
                    </mask>
                  </defs>
                  <g clipPath="url(#sgClip)">
                    {/* Body */}
                    <g filter="url(#sf_ii)">
                      <path
                        d="M72.5706 26.5047C78.7294 8.44572 104.271 8.44574 110.429 26.5048L117.752 47.9745C120.514 56.0741 128.123 61.5188 136.681 61.5188H158.237C177.845 61.5188 185.742 86.8035 169.62 97.9635L153.873 108.863C146.522 113.951 143.441 123.301 146.327 131.763L152.73 150.538C158.956 168.795 138.278 184.417 122.418 173.439L102.883 159.917C96.0346 155.176 86.9654 155.176 80.117 159.917L60.5824 173.439C44.7217 184.417 24.0436 168.795 30.27 150.538L36.673 131.763C39.559 123.301 36.4779 113.951 29.1266 108.863L13.3805 97.9634C-2.74205 86.8035 5.15531 61.5188 24.7635 61.5188H46.3191C54.8767 61.5188 62.4862 56.0741 65.2485 47.9745L72.5706 26.5047Z"
                        fill="#F35C54"
                      />
                    </g>
                    {/* Eyebrows */}
                    <path
                      d="M110 88C113.043 87.512 120.903 84.8288 128 78"
                      stroke="url(#sg0)"
                      strokeWidth="5"
                      strokeLinecap="round"
                    />
                    <path
                      d="M75.5 88C72.457 87.512 64.5967 84.8288 57.5 78"
                      stroke="url(#sg2)"
                      strokeWidth="5"
                      strokeLinecap="round"
                    />
                    {/* Mouth */}
                    <path
                      d="M99.023 114.262C96.9661 114.225 91.4699 115.108 85.9408 118.937"
                      stroke="url(#sg1)"
                      strokeWidth="4"
                      strokeLinecap="round"
                    />
                    {/* Nostrils */}
                    <path
                      d="M124.147 103.046C125.722 103.046 127.041 104.353 126.554 105.852C126.174 107.019 125.558 108.114 124.73 109.072C123.262 110.771 121.22 111.934 118.951 112.363C116.682 112.792 114.326 112.46 112.286 111.424C110.246 110.388 108.647 108.712 107.761 106.681C106.876 104.65 106.759 102.391 107.431 100.288C108.102 98.1846 109.521 96.3676 111.444 95.1466C113.368 93.9255 115.678 93.3758 117.98 93.5913C119.299 93.7147 120.569 94.0848 121.72 94.6706C123.119 95.3822 123.116 97.2349 121.979 98.3158C120.183 100.022 121.39 103.046 123.867 103.046H124.147Z"
                      fill="url(#sg3)"
                    />
                    <path
                      d="M75.1466 103.046C76.7225 103.046 78.0415 104.353 77.5537 105.852C77.1738 107.019 76.5578 108.114 75.7301 109.072C74.2624 110.771 72.22 111.934 69.9509 112.363C67.6818 112.792 65.3264 112.46 63.286 111.424C61.2457 110.388 59.6466 108.712 58.7612 106.681C57.8758 104.65 57.759 102.391 58.4306 100.288C59.1022 98.1846 60.5206 96.3676 62.4443 95.1466C64.3679 93.9255 66.6778 93.3758 68.9802 93.5913C70.2991 93.7147 71.5689 94.0848 72.7205 94.6706C74.1194 95.3822 74.1165 97.2349 72.9786 98.3158C71.183 100.022 72.3903 103.046 74.8671 103.046H75.1466Z"
                      fill="url(#sg4)"
                    />
                    {/* Sunglasses bridge */}
                    <path
                      d="M84.5 94H99.5"
                      stroke="url(#sg5)"
                      strokeWidth="5"
                      strokeLinecap="round"
                    />
                    {/* Sunglasses lenses + reflection */}
                    <g mask="url(#sgMask)">
                      <path
                        d="M42 89C42 86.2386 44.2386 84 47 84H82C84.7614 84 87 86.2386 87 89V102C87 113.046 78.0457 122 67 122H62C50.9543 122 42 113.046 42 102V89Z"
                        fill="url(#sg6)"
                      />
                      <path
                        d="M97 89C97 86.2386 99.2386 84 102 84H137C139.761 84 142 86.2386 142 89V102C142 113.046 133.046 122 122 122H117C105.954 122 97 113.046 97 102V89Z"
                        fill="url(#sg7)"
                      />
                      <rect
                        x="79.5714"
                        y="78.7871"
                        width="10"
                        height="50"
                        transform="rotate(45 79.5714 78.7871)"
                        fill="url(#sg8)"
                      />
                      <rect
                        x="135.355"
                        y="78.7871"
                        width="10"
                        height="50"
                        transform="rotate(45 135.355 78.7871)"
                        fill="url(#sg9)"
                      />
                    </g>
                  </g>
                </svg>
              </div>
              <div
                style={{
                  position: "absolute",
                  bottom: 0,
                  left: 0,
                  right: 0,
                  background: "linear-gradient(transparent, #E8A87C22)",
                  height: 40,
                }}
              />
              {/* Chain overlays - two diagonal chains that sway and break */}
              {/* Chain 1: Top-left diagonal */}
              <div
                style={{
                  position: "absolute",
                  top: -8,
                  left: 30,
                  pointerEvents: "none",
                  transformOrigin: "top center",
                  animation: "chainSwayTL 4.5s ease-in-out infinite",
                }}
              >
                <svg
                  width="40"
                  height="150"
                  viewBox="0 0 18 120"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <defs>
                    <linearGradient
                      id="chainRingOuter1"
                      x1="9"
                      y1="3"
                      x2="9"
                      y2="16"
                      gradientUnits="userSpaceOnUse"
                    >
                      <stop stopColor="#6D6D6D" />
                      <stop offset="1" stopColor="#3F3F3F" />
                    </linearGradient>
                    <linearGradient
                      id="chainRingInner1"
                      x1="9"
                      y1="1"
                      x2="9"
                      y2="18"
                      gradientUnits="userSpaceOnUse"
                    >
                      <stop stopColor="#A8A8A8" />
                      <stop offset="1" stopColor="#5F5F5F" />
                    </linearGradient>
                    <linearGradient
                      id="chainBridgeOuter1"
                      x1="10"
                      y1="14"
                      x2="10"
                      y2="20"
                      gradientUnits="userSpaceOnUse"
                    >
                      <stop stopColor="#6D6D6D" />
                      <stop offset="1" stopColor="#3F3F3F" />
                    </linearGradient>
                    <linearGradient
                      id="chainBridgeInner1"
                      x1="10"
                      y1="14"
                      x2="10"
                      y2="20"
                      gradientUnits="userSpaceOnUse"
                    >
                      <stop stopColor="#A8A8A8" />
                      <stop offset="1" stopColor="#5F5F5F" />
                    </linearGradient>
                  </defs>
                  {/* Rings layer (behind) */}
                  {[0, 1, 2, 3, 4, 5, 6].map((i) => (
                    <g key={`ring-${i}`} transform={`translate(0,${i * 14})`}>
                      <rect
                        x="4"
                        y="3"
                        width="10"
                        height="13"
                        rx="4.5"
                        fill="none"
                        stroke="url(#chainRingOuter1)"
                        strokeWidth="6"
                      />
                      <rect
                        x="4"
                        y="3"
                        width="10"
                        height="13"
                        rx="4.5"
                        fill="none"
                        stroke="url(#chainRingInner1)"
                        strokeWidth="4"
                      />
                    </g>
                  ))}
                  {/* Bridges layer (on top) */}
                  {[0, 1, 2, 3, 4, 5].map((i) => (
                    <g key={`bridge-${i}`} transform={`translate(0,${i * 14})`}>
                      <line
                        x1="12"
                        y1="14"
                        x2="9"
                        y2="20"
                        stroke="url(#chainBridgeOuter1)"
                        strokeWidth="6"
                        strokeLinecap="round"
                      />
                      <line
                        x1="12"
                        y1="14"
                        x2="9"
                        y2="20"
                        stroke="url(#chainBridgeInner1)"
                        strokeWidth="4"
                        strokeLinecap="round"
                      />
                    </g>
                  ))}
                </svg>
              </div>
              {/* Chain 2: Bottom-right diagonal */}
              <div
                style={{
                  position: "absolute",
                  bottom: -24,
                  right: 42,
                  pointerEvents: "none",
                  transformOrigin: "bottom center",
                  animation: "chainSwayBR 4.5s ease-in-out 0.3s infinite",
                }}
              >
                <svg
                  width="40"
                  height="150"
                  viewBox="0 0 18 120"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <defs>
                    <linearGradient
                      id="chainRingOuter2"
                      x1="9"
                      y1="3"
                      x2="9"
                      y2="16"
                      gradientUnits="userSpaceOnUse"
                    >
                      <stop stopColor="#6D6D6D" />
                      <stop offset="1" stopColor="#3F3F3F" />
                    </linearGradient>
                    <linearGradient
                      id="chainRingInner2"
                      x1="9"
                      y1="1"
                      x2="9"
                      y2="18"
                      gradientUnits="userSpaceOnUse"
                    >
                      <stop stopColor="#A8A8A8" />
                      <stop offset="1" stopColor="#5F5F5F" />
                    </linearGradient>
                    <linearGradient
                      id="chainBridgeOuter2"
                      x1="10"
                      y1="14"
                      x2="10"
                      y2="20"
                      gradientUnits="userSpaceOnUse"
                    >
                      <stop stopColor="#6D6D6D" />
                      <stop offset="1" stopColor="#3F3F3F" />
                    </linearGradient>
                    <linearGradient
                      id="chainBridgeInner2"
                      x1="10"
                      y1="14"
                      x2="10"
                      y2="20"
                      gradientUnits="userSpaceOnUse"
                    >
                      <stop stopColor="#A8A8A8" />
                      <stop offset="1" stopColor="#5F5F5F" />
                    </linearGradient>
                  </defs>
                  {/* Rings layer (behind) */}
                  {[0, 1, 2, 3, 4, 5, 6].map((i) => (
                    <g key={`ring-${i}`} transform={`translate(0,${i * 14})`}>
                      <rect
                        x="4"
                        y="3"
                        width="10"
                        height="13"
                        rx="4.5"
                        fill="none"
                        stroke="url(#chainRingOuter2)"
                        strokeWidth="6"
                      />
                      <rect
                        x="4"
                        y="3"
                        width="10"
                        height="13"
                        rx="4.5"
                        fill="none"
                        stroke="url(#chainRingInner2)"
                        strokeWidth="4"
                      />
                    </g>
                  ))}
                  {/* Bridges layer (on top) */}
                  {[0, 1, 2, 3, 4, 5].map((i) => (
                    <g key={`bridge-${i}`} transform={`translate(0,${i * 14})`}>
                      <line
                        x1="12"
                        y1="14"
                        x2="9"
                        y2="20"
                        stroke="url(#chainBridgeOuter2)"
                        strokeWidth="6"
                        strokeLinecap="round"
                      />
                      <line
                        x1="12"
                        y1="14"
                        x2="9"
                        y2="20"
                        stroke="url(#chainBridgeInner2)"
                        strokeWidth="4"
                        strokeLinecap="round"
                      />
                    </g>
                  ))}
                </svg>
              </div>
              {/* Break spark effect */}
              <div
                style={{
                  position: "absolute",
                  top: "6%",
                  left: "16%",
                  width: 24,
                  height: 24,
                  marginTop: -12,
                  marginLeft: -12,
                  borderRadius: "100%",
                  background:
                    "radial-gradient(circle, rgba(255,220,150,0.9) 0%, rgba(255,180,80,0.4) 40%, transparent 70%)",
                  animation: "chainSpark 4.5s ease-in-out infinite",
                  pointerEvents: "none",
                }}
              />
            </div>
            {/* Rank badge - outside overflow:hidden container */}
            <div
              style={{
                position: "absolute",
                top: -6,
                right: -6,
                background: "#EF4444",
                color: "#fff",
                fontFamily: "'Press Start 2P', monospace",
                fontSize: 7,
                padding: "3px 6px",
                borderRadius: 2,
                boxShadow: "0 0 8px #EF444466",
                zIndex: 1,
              }}
            >
              S+
            </div>
          </div>

          {/* Name & info */}
          <div style={{ flex: 1, minWidth: 0 }}>
            <div
              style={{
                fontFamily: "'Press Start 2P', monospace",
                fontSize: 8,
                color: "#E8A87C88",
                letterSpacing: 3,
                marginBottom: 4,
              }}
            >
              DEEP SEA COLOSSEUM
            </div>
            <h1
              style={{
                fontFamily: "'Black Han Sans', sans-serif",
                fontSize: 38,
                margin: "0 0 2px",
                background:
                  "linear-gradient(90deg, #EF4444, #F59E0B, #E8A87C, #EF4444)",
                backgroundSize: "200% auto",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
                backgroundClip: "text",
                animation: glitch ? "none" : "textShimmer 4s linear infinite",
                textShadow: "none",
                filter: glitch ? "hue-rotate(90deg) brightness(1.3)" : "none",
                transition: "filter 0.1s",
              }}
            >
              {d.name}
            </h1>
            <div
              style={{
                fontFamily: "'Press Start 2P', monospace",
                fontSize: 11,
                background:
                  "linear-gradient(90deg, #E8A87C, #EF4444, #F59E0B, #E8A87C)",
                backgroundSize: "200% auto",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
                backgroundClip: "text",
                animation: "textShimmer 3s linear infinite",
                letterSpacing: 3,
                marginBottom: 12,
              }}
            >
              {d.ringName}
            </div>

            {/* Info grid */}
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
                gap: "4px 16px",
                fontSize: 11,
              }}
            >
              {[
                ["출신", d.origin],
                ["체중", d.weight],
                ["체고", d.height],
                ["스타일", d.style],
              ].map(([label, val]) => (
                <div key={label} style={{ display: "flex", gap: 8 }}>
                  <span
                    style={{
                      color: "#555",
                      fontFamily: "monospace",
                      fontSize: 10,
                    }}
                  >
                    {label}
                  </span>
                  <span style={{ color: "#bbb" }}>{val}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Quote */}
        <div
          style={{
            background: "linear-gradient(90deg, #E8A87C11, transparent)",
            borderLeft: "3px solid #E8A87C",
            padding: "10px 14px",
            marginBottom: 20,
            opacity: loaded ? 1 : 0,
            transition: "opacity 0.8s ease 0.3s",
          }}
        >
          <div
            style={{
              fontSize: 13,
              color: "#E8A87C",
              fontStyle: "italic",
              lineHeight: 1.6,
            }}
          >
            "{d.quote}"
          </div>
        </div>

        {/* Tab navigation */}
        <div
          style={{
            display: "flex",
            gap: 0,
            marginBottom: 16,
            borderBottom: "1px solid #222",
          }}
        >
          {[
            { id: "profile", label: "PROFILE" },
            { id: "moves", label: "MOVE LIST" },
            { id: "lore", label: "LORE" },
          ].map((t) => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              style={{
                fontFamily: "'Press Start 2P', monospace",
                fontSize: 8,
                padding: "10px 16px",
                background: tab === t.id ? "#E8A87C11" : "transparent",
                color: tab === t.id ? "#E8A87C" : "#555",
                border: "none",
                borderBottom:
                  tab === t.id ? "2px solid #E8A87C" : "2px solid transparent",
                cursor: "pointer",
                letterSpacing: 1,
                transition: "all 0.2s",
              }}
            >
              {t.label}
            </button>
          ))}
        </div>

        {/* Tab content */}
        <div
          style={{
            animation: "fadeSlide 0.3s ease",
            minHeight: 300,
            paddingBottom: 40,
          }}
        >
          {/* ===== PROFILE TAB ===== */}
          {tab === "profile" && (
            <div style={{ display: "flex", gap: 24, flexWrap: "wrap" }}>
              {/* Stats radar */}
              <div style={{ flex: "1 1 200px", minWidth: 200 }}>
                <div
                  style={{
                    fontFamily: "'Press Start 2P', monospace",
                    fontSize: 8,
                    color: "#555",
                    marginBottom: 12,
                    letterSpacing: 2,
                  }}
                >
                  FIGHTER STATS
                </div>
                <StatBar label="POWER" value={d.stats.power} color="#EF4444" />
                <StatBar label="SPEED" value={d.stats.speed} color="#F59E0B" />
                <StatBar label="DEF" value={d.stats.defense} color="#60A5FA" />
                <StatBar
                  label="TECH"
                  value={d.stats.technique}
                  color="#A78BFA"
                />
                <StatBar
                  label="RECOV"
                  value={d.stats.recovery}
                  color="#34D399"
                />
                <StatBar
                  label="ESCAPE"
                  value={d.stats.escape}
                  color="#E8A87C"
                />

                <div
                  style={{
                    marginTop: 16,
                    padding: 10,
                    background: "rgba(232,168,124,0.05)",
                    border: "1px solid rgba(232,168,124,0.1)",
                    borderRadius: 4,
                  }}
                >
                  <div
                    style={{
                      fontFamily: "'Press Start 2P', monospace",
                      fontSize: 7,
                      color: "#E8A87C",
                      marginBottom: 6,
                    }}
                  >
                    ⚠ REFEREE NOTE
                  </div>
                  <div style={{ fontSize: 10, color: "#888", lineHeight: 1.5 }}>
                    관절기 무효. 급소 없음. KO 판정 기준 현재 규정위원회 검토
                    중. 상대 선수 보험 가입 권장.
                  </div>
                </div>
              </div>

              {/* Likes / Dislikes */}
              <div style={{ flex: "1 1 200px", minWidth: 200 }}>
                <div style={{ marginBottom: 16 }}>
                  <div
                    style={{
                      fontFamily: "'Press Start 2P', monospace",
                      fontSize: 8,
                      color: "#34D399",
                      marginBottom: 8,
                      letterSpacing: 2,
                    }}
                  >
                    ♥ LIKES
                  </div>
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                    {d.likes.map((l) => (
                      <Tag key={l} label={l} color="#34D399" />
                    ))}
                  </div>
                </div>
                <div style={{ marginBottom: 16 }}>
                  <div
                    style={{
                      fontFamily: "'Press Start 2P', monospace",
                      fontSize: 8,
                      color: "#EF4444",
                      marginBottom: 8,
                      letterSpacing: 2,
                    }}
                  >
                    ✕ DISLIKES
                  </div>
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                    {d.dislikes.map((l) => (
                      <Tag key={l} label={l} color="#EF4444" />
                    ))}
                  </div>
                </div>

                <div
                  style={{
                    fontFamily: "'Press Start 2P', monospace",
                    fontSize: 8,
                    color: "#555",
                    marginBottom: 8,
                    letterSpacing: 2,
                  }}
                >
                  BIO
                </div>
                {d.bio.map((line, i) => (
                  <div
                    key={i}
                    style={{
                      fontSize: 12,
                      color: "#aaa",
                      lineHeight: 1.7,
                      paddingLeft: 10,
                      borderLeft: "1px solid #333",
                      marginBottom: 4,
                    }}
                  >
                    {line}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* ===== MOVES TAB ===== */}
          {tab === "moves" && (
            <div>
              <div
                style={{
                  fontFamily: "'Press Start 2P', monospace",
                  fontSize: 8,
                  color: "#555",
                  marginBottom: 12,
                  letterSpacing: 2,
                }}
              >
                SIGNATURE MOVES — SELECT TO VIEW DETAILS
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                {d.moves.map((move, i) => (
                  <MoveCard
                    key={i}
                    move={move}
                    index={i}
                    isActive={activeMove === i}
                    onClick={() => setActiveMove(i)}
                  />
                ))}
              </div>

              <div
                style={{
                  marginTop: 20,
                  padding: 12,
                  background: "rgba(239,68,68,0.05)",
                  border: "1px solid rgba(239,68,68,0.15)",
                  borderRadius: 4,
                }}
              >
                <div
                  style={{
                    fontFamily: "'Press Start 2P', monospace",
                    fontSize: 7,
                    color: "#EF4444",
                    marginBottom: 6,
                  }}
                >
                  💀 FINISHER ADVISORY
                </div>
                <div style={{ fontSize: 10, color: "#888", lineHeight: 1.6 }}>
                  CARDIAC STOMACH DROP은 실제 불가사리의 체외소화 메커니즘을
                  기반으로 합니다. 위(stomach)를 입 밖으로 뒤집어 상대에게
                  밀착시킨 뒤 소화액으로 분해합니다. 시청자 민원이 가장 많은
                  기술이지만 자연 다큐멘터리에서는 매일 방영됩니다.
                </div>
              </div>

              <div
                style={{
                  marginTop: 12,
                  padding: 12,
                  background: "rgba(96,165,250,0.05)",
                  border: "1px solid rgba(96,165,250,0.15)",
                  borderRadius: 4,
                }}
              >
                <div
                  style={{
                    fontFamily: "'Press Start 2P', monospace",
                    fontSize: 7,
                    color: "#60A5FA",
                    marginBottom: 6,
                  }}
                >
                  🎙️ 해설석
                </div>
                <div
                  style={{
                    fontSize: 11,
                    color: "#999",
                    lineHeight: 1.6,
                    fontStyle: "italic",
                  }}
                >
                  "저 선수는 뼈대 자체가 유동적입니다. 관절기가 안 먹혀요.
                  어떻게 이기라는 겁니까?"
                </div>
              </div>
            </div>
          )}

          {/* ===== LORE TAB ===== */}
          {tab === "lore" && (
            <div>
              <div
                style={{
                  fontFamily: "'Press Start 2P', monospace",
                  fontSize: 8,
                  color: "#555",
                  marginBottom: 12,
                  letterSpacing: 2,
                }}
              >
                BACKSTORY FILES
              </div>
              <div style={{ display: "flex", gap: 4, marginBottom: 16 }}>
                {d.lore.map((l, i) => (
                  <button
                    key={i}
                    onClick={() => setActiveLore(i)}
                    style={{
                      fontFamily: "'Press Start 2P', monospace",
                      fontSize: 7,
                      padding: "8px 12px",
                      background:
                        activeLore === i
                          ? "#E8A87C15"
                          : "rgba(255,255,255,0.02)",
                      color: activeLore === i ? "#E8A87C" : "#555",
                      border: `1px solid ${
                        activeLore === i ? "#E8A87C44" : "#1a1a1a"
                      }`,
                      cursor: "pointer",
                      borderRadius: 3,
                      transition: "all 0.2s",
                    }}
                  >
                    FILE {String(i + 1).padStart(2, "0")}
                  </button>
                ))}
              </div>
              <div
                style={{ animation: "fadeSlide 0.25s ease" }}
                key={activeLore}
              >
                <div
                  style={{
                    background: "rgba(255,255,255,0.02)",
                    border: "1px solid #1a1a1a",
                    padding: 16,
                    borderRadius: 4,
                  }}
                >
                  <div
                    style={{
                      fontFamily: "'Black Han Sans', sans-serif",
                      fontSize: 18,
                      color: "#E8A87C",
                      marginBottom: 4,
                    }}
                  >
                    {d.lore[activeLore].title}
                  </div>
                  <div
                    style={{
                      fontSize: 12,
                      color: "#aaa",
                      lineHeight: 1.8,
                    }}
                  >
                    {d.lore[activeLore].text}
                  </div>
                </div>
              </div>

              {/* Timeline */}
              <div style={{ marginTop: 24 }}>
                <div
                  style={{
                    fontFamily: "'Press Start 2P', monospace",
                    fontSize: 8,
                    color: "#555",
                    marginBottom: 12,
                    letterSpacing: 2,
                  }}
                >
                  CAREER TIMELINE
                </div>
                {[
                  {
                    year: "???",
                    event: "동해 울진 앞바다에서 태어남. 정확한 생년 불명.",
                  },
                  {
                    year: "2024",
                    event:
                      '예능 프로그램 출연. 의자 탈출 실험. "소용없다" 밈 탄생.',
                  },
                  {
                    year: "2024",
                    event: "심해 콜로세움 데뷔. 첫 상대: 해삼. 3초 TKO.",
                  },
                  {
                    year: "2025",
                    event: "무패 행진. 문어와의 8강전에서 먹물 속 탈출로 화제.",
                  },
                  {
                    year: "2025",
                    event:
                      "고래와의 체급 초월 매치. 150톤 바디슬램 후에도 재생.",
                  },
                  {
                    year: "2026",
                    event: '"은퇴 불가" 판정. 규정위원회 특별 조항 신설.',
                  },
                ].map((item, i) => (
                  <div
                    key={i}
                    style={{
                      display: "flex",
                      gap: 12,
                      marginBottom: 0,
                      padding: "8px 0",
                      borderLeft: "1px solid #E8A87C33",
                      marginLeft: 20,
                      paddingLeft: 16,
                      position: "relative",
                    }}
                  >
                    <div
                      style={{
                        position: "absolute",
                        left: -4,
                        top: 10,
                        width: 7,
                        height: 7,
                        borderRadius: "50%",
                        background: "#E8A87C",
                        boxShadow: "0 0 6px #E8A87C44",
                      }}
                    />
                    <div
                      style={{
                        fontFamily: "monospace",
                        fontSize: 10,
                        color: "#E8A87C",
                        minWidth: 32,
                      }}
                    >
                      {item.year}
                    </div>
                    <div
                      style={{ fontSize: 11, color: "#999", lineHeight: 1.5 }}
                    >
                      {item.event}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Bottom bar */}
      <div
        style={{
          position: "fixed",
          bottom: 0,
          left: 0,
          right: 0,
          zIndex: 50,
          background: "linear-gradient(transparent, #08080C 40%)",
          padding: "20px 16px 12px",
        }}
      >
        <div
          style={{
            maxWidth: 800,
            margin: "0 auto",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <div
            style={{
              fontFamily: "'Press Start 2P', monospace",
              fontSize: 7,
              color: "#333",
            }}
          >
            ◀ PREV FIGHTER
          </div>
          <div
            style={{
              fontFamily: "'Press Start 2P', monospace",
              fontSize: 8,
              color: "#E8A87C",
              animation: "blink 1.2s step-end infinite",
            }}
          >
            PRESS START
          </div>
          <div
            style={{
              fontFamily: "'Press Start 2P', monospace",
              fontSize: 7,
              color: "#333",
            }}
          >
            NEXT FIGHTER ▶
          </div>
        </div>
      </div>
    </div>
  );
}
